import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Center,
  chakra,
  Flex,
  Icon,
  Spinner,
  Table as ChakraTable,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { isEmpty, isFunction, noop } from 'lodash-es';

import React, { forwardRef, useEffect, useRef } from 'react';
import { FiInbox } from 'react-icons/fi';
import {
  useRowSelect,
  useSortBy,
  useTable,
  usePagination,
} from 'react-table';
import 'regenerator-runtime/runtime';

import ActionButton from 'components/Table/ActionButton';
import BulkActions from 'components/Table/BulkActions';
import GlobalFilter from 'components/Table/GlobalFilter';
import Pagination from './Pagination';

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <>
      <input type="checkbox" ref={resolvedRef} {...rest} />
    </>
  );
});

const compareIgnoreCase = (a, b) => {
  const r1 = typeof a === 'string' ? a?.toLowerCase() : a;
  const r2 = typeof b === 'string' ? b?.toLowerCase() : b;
  if (r1 < r2) {
    return -1;
  }
  if (r1 > r2) {
    return 1;
  }
  return 0;
};

const rowProps = (onRowClick, row) => {
  return onRowClick
    ? {
      as: 'button',
      width: '100%',
      display: 'flex',
      onClick: e => {
        if (e.target.closest('button').getAttribute('role') === 'row') {
          onRowClick(row.id);
        }
      },
    }
    : {};
};

const Table = ({
  isLoading,
  data = [],
  isUpdating,
  columns,
  rowActions,
  bulkOptions,
  onRowClick,
  fetchData = noop,
  pageCount: controlledPageCount,
  initialPageSize = 10,
}) => {

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    state: { selectedRowIds, pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: data || [],
      initialState: { pageIndex: 0, pageSize: initialPageSize }, // Pass our hoisted table state
      manualPagination: true, // Tell the usePagination
      pageCount: controlledPageCount,
      autoResetPage: false,
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => {
          return compareIgnoreCase(
            row1.values[columnName],
            row2.values[columnName]
          );
        },
      },
    },
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push(columns => [
        ...(bulkOptions
          ? [
            {
              id: 'selection',
              Header: ({ getToggleAllRowsSelectedProps }) => (
                <div>
                  <IndeterminateCheckbox
                    data-testid="Toggle All"
                    {...getToggleAllRowsSelectedProps()}
                  />
                </div>
              ),
              disableSortBy: true,
              Cell: ({ row }) => (
                <div>
                  <IndeterminateCheckbox
                    data-testid={`Toggle ${row.index}`}
                    {...row.getToggleRowSelectedProps()}
                  />
                </div>
              ),
            },
          ]
          : []),
        ...columns,
        ...(!isEmpty(rowActions) || isFunction(rowActions)
          ? [
            {
              id: 'actions',
              Header: 'Actions',
              disableSortBy: true,
              Cell: ({ row }) => <ActionButton {...{ row, rowActions }} />,
            },
          ]
          : []),
      ]);
    },
  );

  React.useEffect(() => {
    console.log('fetch')
    console.log(pageIndex, pageSize)

    fetchData(pageIndex + 1, pageSize)
  }, [fetchData, pageIndex, pageSize])

  return (
    <>
      {/* <Flex
        justifyContent="space-between"
        alignItems="center"
        height="70px"
        position="sticky"
        top="-16px"
        left="0"
        zIndex={6}
        bg="gray.100"
        width="100%"
        _after={{
          content: '""',
          width: 'calc(100% + 30px)',
          zIndex: 0,
          height: '100%',
          position: 'absolute',
          bg: 'gray.100',
          left: '-15px',
        }}
      >
        {bulkOptions && (
          <BulkActions
            {...{
              bulkOptions,
              selectedFlatRows,
              selectedRowIds,
              disabled: isLoading,
            }}
          />
        )}
      </Flex> */}
      <Box bg="white" rounded="md" minW="fit-content" py={4}>
        {!data?.length && (
          <Center py={2} color="gray.500">
            {isLoading ? (
              <Spinner my={8} />
            ) : (
              <Text>
                <Icon as={FiInbox} mr={2} /> No data
              </Text>
            )}
          </Center>
        )}
        {data?.length > 0 && (
          <>
            <ChakraTable
              {...getTableProps()}
              variant="simple"
              {...(isUpdating && {
                cursor: 'progress',
                opacity: '0.7',
              })}
            >
              <Thead position="sticky" top="50px" bg="#fff" zIndex={4}>
                {headerGroups.map(headerGroup => (
                  <Tr
                    whiteSpace="nowrap"
                    {...headerGroup.getHeaderGroupProps()}
                  >
                    {headerGroup.headers.map(column => (
                      <Th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        isNumeric={column.isNumeric}
                      >
                        {column.render('Header')}
                        {!column.disableSortBy && (
                          <chakra.span pl="4">
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <TriangleDownIcon aria-label="sorted descending" />
                              ) : (
                                <TriangleUpIcon aria-label="sorted ascending" />
                              )
                            ) : (
                              <TriangleUpIcon
                                aria-label="sorted descending"
                                opacity={0.25}
                              />
                            )}
                          </chakra.span>
                        )}
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody {...getTableBodyProps()}>
                {page.map(row => {
                  prepareRow(row);
                  return (
                    <Tr
                      {...rowProps(onRowClick, row.original)}
                      {...row.getRowProps()}
                    >
                      {row.cells.map(cell => (
                        <Td
                          width={onRowClick ? '100%' : 'auto'}
                          {...cell.getCellProps()}
                          isNumeric={cell.column.isNumeric}
                        >
                          {cell.render('Cell')}
                        </Td>
                      ))}
                    </Tr>
                  );
                })}
              </Tbody>
            </ChakraTable>
          </>
        )}
      </Box>
      {controlledPageCount && <Pagination {...{
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        pageSize,
        pageIndex,
      }} />}
    </>
  );
};


export default Table;
